import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getAssetTypeIcon, getAssetTypeName } from '../../../data/assets/helpers';
import styles from '../Settings.module.scss';
import { TpTemplateInfo } from '../../../api/models/assets';
import { formatDate } from '../../../components/miscUtils';

interface TemplateCardProps {
  template: TpTemplateInfo;
  scopeValue: string;
  ontology: { assetTypes: Record<string, any> };
  cardWidth: number;
  isInvalid: boolean;
  isModifiable: boolean;
  isSystemAdmin: boolean;
  onEdit: (template: TpTemplateInfo) => void;
  onModifyMetadata: (template: TpTemplateInfo) => void;
  onDelete: (template: TpTemplateInfo) => void;
  onToggleActive?: (template: TpTemplateInfo) => void;
  onSetDefault?: (template: TpTemplateInfo) => void;
}

interface SetDefaultButtonProps {
  template: TpTemplateInfo;
  isAssetTemplate: boolean;
  onSetDefault?: (template: TpTemplateInfo) => void;
}

/**
 * Helper button to set a template as the global default.
 */
function SetDefaultButton({ template, isAssetTemplate, onSetDefault }: SetDefaultButtonProps) {
  return (
    /*
     * Note: button[disabled] has opacity: 0.5 in globals.
     * This makes the solid star less bright than a non-disabled solid star.
     */
    <button
      type="button"
      className={styles.templateCardIconButton}
      disabled={isAssetTemplate || template.isDefault}
      title={
        isAssetTemplate
          ? 'Cannot set asset-specific templates as global default'
          : template.isDefault
            ? 'Already default'
            : 'Set as Default'
      }
      onClick={() => onSetDefault?.(template)}
    >
      <FontAwesomeIcon icon={[template.isDefault ? 'fas' : 'far', 'star']} />
    </button>
  );
}

interface ToggleActiveButtonProps {
  template: TpTemplateInfo;
  isAssetTemplate: boolean;
  isActive: boolean;
  onToggleActive?: (template: TpTemplateInfo) => void;
}

/**
 * Helper button to toggle whether a template is active/visible in the platform.
 */
function ToggleActiveButton({ template, isAssetTemplate, isActive, onToggleActive }: ToggleActiveButtonProps) {
  return (
    <button
      type="button"
      className={styles.templateCardIconButton}
      disabled={isAssetTemplate}
      title={isAssetTemplate ? 'Asset templates cannot be deactivated' : isActive ? 'Deactivate' : 'Activate'}
      onClick={() => onToggleActive?.(template)}
    >
      <FontAwesomeIcon icon={['fas', isActive ? 'eye' : 'eye-slash']} />
    </button>
  );
}

/**
 * Renders a single template card, used both for valid and invalid (unparsable YAML) templates
 * in the {@link TemplateOverview} grid. Invalid templates additionally show a warning icon next
 * to their name and always use a generic icon (their `templateType` may not map to a known asset type).
 */
export function TemplateCard({
  template,
  scopeValue,
  ontology,
  cardWidth,
  isInvalid,
  isModifiable,
  isSystemAdmin,
  onEdit,
  onModifyMetadata,
  onDelete,
  onToggleActive,
  onSetDefault,
}: TemplateCardProps) {
  const assetTypeOntology = ontology.assetTypes[template.templateType];
  const iconName =
    isInvalid || scopeValue === 'ontologies' || !assetTypeOntology
      ? 'chart-simple'
      : getAssetTypeIcon(assetTypeOntology);

  const isAssetTemplate = template.assetId !== undefined;
  const isActive = template.isActive !== false;

  const hoverInfo = [
    `Internal name: ${template.name}`,
    template.version ? `Version: ${template.version}` : null,
    isAssetTemplate ? `Asset ID: ${template.assetId}` : null,
    template.updatedAt ? `Last updated: ${formatDate(template.updatedAt)}` : null,
    template.createdAt ? `Created at: ${formatDate(template.createdAt)}` : null,
    `Scope: ${template.scope}`,
    `Type: ${template.templateType}`,
    template.isSystem ? 'System template' : null,
    template.isDefault ? `Default template for ${template.templateType}` : null,
    template.isActive === false ? 'Inactive template' : null,
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <div
      className={`${styles.templateCard} ${!isActive ? styles.inactiveCard : ''}`}
      style={{ width: cardWidth }}
      title={hoverInfo}
    >
      <div className={styles.templateCardHeader}>
        <div className={styles.templateCardIcon}>
          <FontAwesomeIcon icon={['fad', iconName as any]} />
        </div>

        <div className={styles.templateCardType}>
          {assetTypeOntology ? getAssetTypeName(assetTypeOntology) : template.templateType}
        </div>

        {isModifiable && !isInvalid && !isAssetTemplate && (
          <div className={styles.templateCardActions}>
            <SetDefaultButton template={template} isAssetTemplate={isAssetTemplate} onSetDefault={onSetDefault} />

            <ToggleActiveButton
              template={template}
              isAssetTemplate={isAssetTemplate}
              isActive={isActive}
              onToggleActive={onToggleActive}
            />
          </div>
        )}

        <div className={styles.templateCardGearButton} onClick={() => onEdit(template)} title="Edit YAML">
          <FontAwesomeIcon icon={['fas', 'gear']} />
        </div>

        {isModifiable && !isAssetTemplate && (!template.isSystem || isSystemAdmin) && (
          <div
            className={styles.templateCardEditButton}
            title="Edit display name and version"
            onClick={() => onModifyMetadata(template)}
          >
            <FontAwesomeIcon icon={['fas', 'pencil']} />
          </div>
        )}

        {isModifiable && !isAssetTemplate && (!template.isSystem || isSystemAdmin) && (
          <div className={styles.templateCardDeleteButton} onClick={() => onDelete(template)} title="Delete">
            <FontAwesomeIcon icon={['fas', 'trash-can']} />
          </div>
        )}
      </div>

      <div className={styles.templateCardNameBig}>
        {template.displayName || template.name}
        {isInvalid && (
          <FontAwesomeIcon icon={['fas', 'triangle-exclamation']} className={styles.invalidIcon} title="Invalid YAML" />
        )}
      </div>

      <div className={styles.templateCardFooter}>
        <div className={styles.badgeContainer}>
          <span className={styles.badge}>{template.scope}</span>
          {template.isSystem && <span className={styles.badge}>System</span>}
          {isAssetTemplate && <span className={styles.badge}>Asset-specific</span>}
        </div>
        {template.updatedAt && (
          <div className={styles.templateCardDate} title={`Updated at ${formatDate(template.updatedAt)}`}>
            {formatDate(template.updatedAt)}
          </div>
        )}
      </div>
    </div>
  );
}

export default TemplateCard;
